import { AfterViewInit, Directive, ElementRef, HostBinding, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { isDefined, isEqual } from '@fi-shared/utils';
import { isNullOrUndefined } from '@fi-shared/utils/is-null-or-undefined';

@Directive()
export abstract class FiColumnsContainerBaseDirective extends FiComponentsContainer implements OnInit, AfterViewInit {
    // we use it in fi-content-validator as scrollable element
    @ViewChild('leftSideContainer') leftSideContainer: ElementRef<HTMLDivElement>;
    @ViewChild('rightSideContainer') rightSideContainer: ElementRef<HTMLDivElement>;
    @ViewChild('centerContainer') centerContainer: ElementRef<HTMLDivElement>;
    @ViewChild('columnsContainer') columnsContainer: ElementRef<HTMLDivElement>; // use it in case [sideContainersStickyMode]="true"
    //
    @HostBinding('class.side-containers-sticky-mode') @Input() sideContainersStickyMode: boolean;

    @Input() preventChatOverflow: boolean = false;
    @Input() isSingleScrollForAllContainers: boolean;

    private componentResizeObserver: ResizeObserver;
    private stickyContainersResizeObserver: ResizeObserver;

    private mutationObserver: MutationObserver;

    private readonly columnsContainerScrollEventListenerAttrName: string = 'scroll-event-listener';
    private stickyElements: HTMLElement[] = [];
    private lastScrollHeightsMap = new Map<HTMLElement, number>();

    private readonly elementRef = inject(ElementRef);
    private readonly containerWidthCrossingWithChatButtonTwoColumns: number = 1390;
    private readonly containerWidthCrossingWithChatButtonThreeColumns: number = 1550;
    private currentContainerWidthCrossingWithChatButton: number;
    private readonly rightSideContainerFullHeightClassName: string = 'right-side-full-height-container';

    public ngOnInit(): void {
        this.setCurrentContainerWidthCrossingWithChatButton();

        if (!this.preventChatOverflow) {
            this.elementRef.nativeElement.classList.add(this.rightSideContainerFullHeightClassName);
        } else {
            this.subscribeOnResize();
        }
    }

    private setCurrentContainerWidthCrossingWithChatButton(): void {
        const component: HTMLElement = this.elementRef.nativeElement;
        this.currentContainerWidthCrossingWithChatButton = component.classList.contains('fi-three-columns-container')
            ? this.containerWidthCrossingWithChatButtonThreeColumns
            : this.containerWidthCrossingWithChatButtonTwoColumns;
    }

    private handleScroll: () => void = () => {
        const containerScrollTop: number = this.columnsContainer.nativeElement.scrollTop;
        this.stickyElements.forEach((element: HTMLElement) => {
            const stickyElOffsetHeight: number = element.offsetHeight;
            const stickyElScrollHeight: number = element.scrollHeight;

            const isScrollToBottom: boolean = containerScrollTop + stickyElOffsetHeight > stickyElScrollHeight;

            element.scrollTop = isScrollToBottom ? stickyElScrollHeight - stickyElOffsetHeight : containerScrollTop;
        });
    };

    public ngAfterViewInit(): void {
        setTimeout(() => {
            this.setContainerScrollHandler();
        });
    }

    private setStickyElStyle(): void {
        let elements: HTMLElement[] = (Array.from(this.columnsContainer.nativeElement.children) as HTMLElement[]).map(
            (element: HTMLElement) => {
                const forceScrollableElement: HTMLElement = element.querySelector('[forceScrollableElement]');
                return isNullOrUndefined(forceScrollableElement) ? element : forceScrollableElement;
            },
        );

        const maxScrollHeight: number = Math.max(...elements.map((el: HTMLElement) => el.scrollHeight));
        let mainIndex: number = elements.findIndex((el: HTMLElement) => el.scrollHeight === maxScrollHeight);
        if (mainIndex === -1) {
            mainIndex = 0;
        }
        const mainEl: HTMLElement = elements[mainIndex];
        const rightSideElement: HTMLElement = elements[elements.length - 1];
        elements.splice(mainIndex, 1);
        this.stickyElements = [...elements];
        mainEl.classList.add('sticky-mod-main-el');
        mainEl.classList.remove('sticky-mod-sticky-el', 'sticky-mod-sticky-el-right');

        this.stickyElements.forEach((stickyEl: HTMLElement) => {
            stickyEl.classList.add('sticky-mod-sticky-el');
            stickyEl.classList.remove('sticky-mod-main-el');
            if (isEqual(rightSideElement, stickyEl) && this.preventChatOverflow)
                stickyEl.classList.add('sticky-mod-sticky-el-right');
        });
    }

    private observeStickyElementsChange(): void {
        const onElementSizeChange = () => {
            this.stickyElements.forEach((element: HTMLElement) => {
                const newScrollHeight: number = element.scrollHeight;
                const lastScrollHeight: number = this.lastScrollHeightsMap.get(element) || 0;
                if (lastScrollHeight !== newScrollHeight) {
                    this.lastScrollHeightsMap.set(element, newScrollHeight);
                    this.setContainerScrollHandler();
                }
            });
        };
        this.mutationObserver = new MutationObserver(onElementSizeChange);
        this.stickyContainersResizeObserver = new ResizeObserver(onElementSizeChange);

        this.stickyElements.forEach((element: HTMLElement) => {
            this.lastScrollHeightsMap.set(element, element.scrollHeight);
            this.mutationObserver.observe(element, { attributes: true, childList: true, subtree: true });
            this.stickyContainersResizeObserver.observe(element);
        });
    }

    private setContainerScrollHandler(): void {
        if (!this.isSingleScrollForAllContainers) {
            return;
        }

        this.setStickyElStyle();
        this.observeStickyElementsChange();

        const isStickyScrollable: boolean = this.isContentOverflowing();
        this.columnsContainer.nativeElement.toggleAttribute(
            this.columnsContainerScrollEventListenerAttrName,
            isStickyScrollable,
        );
        if (isStickyScrollable) {
            this.columnsContainer.nativeElement.addEventListener('scroll', this.handleScroll);
        } else {
            this.columnsContainer.nativeElement.removeEventListener('scroll', this.handleScroll);
        }
    }

    private isContentOverflowing(): boolean {
        return this.stickyElements.some((element: HTMLElement) => element.offsetHeight < element.scrollHeight);
    }

    private subscribeOnResize(): void {
        this.componentResizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
            for (const entry of entries) {
                const isRightSideContainerFullHeight: boolean =
                    entry.contentRect.width > this.currentContainerWidthCrossingWithChatButton;
                this.elementRef.nativeElement.classList.toggle(
                    this.rightSideContainerFullHeightClassName,
                    isRightSideContainerFullHeight,
                );
            }
        });
        this.componentResizeObserver.observe(this.elementRef.nativeElement);
    }

    public ngOnDestroy(): void {
        [this.componentResizeObserver, this.mutationObserver, this.stickyContainersResizeObserver]
            .filter(isDefined)
            .forEach((observer) => observer.disconnect());
    }
}
